import { FC, useCallback, useMemo, useState } from 'react';
import { API_PREFIX_LEGACY } from '../../const/api';
import { Client, useAnalyticsContext } from '@lemonade-hq/nuiza';
import styled from 'styled-components';
import { Flex, font, preventOneWordInLastLine, themedColor } from '@lemonade-hq/cdk';
import parse from 'html-react-parser';
import { Button, InfoIcon, InputText, MEDIA_QUERIES, Option, OptionsList, Select } from '@lemonade-hq/nuis';
import Image from 'next/image';
import bgImage from '@/static/assets/fsd/bg.svg';
import { US_STATES } from '@/components/lemonadeGoesGlobal/formData';
import { useGeoLookupContext } from '@/hooks/useGeoLookupContext';

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Container = styled(Flex)`
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  flex: 1;
  overflow: hidden;
`;

const FormSection = styled(Flex)`
  flex-direction: column;
  align-items: center;
  padding: 16px 20px;
  z-index: 1;

  ${MEDIA_QUERIES.tabletAndDesktop`
    padding: 32px 20px;
  `}
`;

const Title = styled.h1`
  ${font('alt', { fontSize: '2.8rem', lineHeight: '36px', fontWeight: 400 })};
  text-align: center;
  white-space: pre-line;
  color: #4a4a4a;

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('alt', { fontSize: '4.8rem', lineHeight: '66px', fontWeight: 400 })};
  `}
`;

const Subtitle = styled.p`
  ${font('main', { fontSize: '1.8rem', lineHeight: '28px', fontWeight: 400 })};
  text-align: center;
  margin-top: 4px;
  color: #4a4a4a;

  ${MEDIA_QUERIES.tabletAndDesktop`
    margin-top: 12px;
  `}
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 560px;
  margin-top: 32px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    margin-top: 48px;
    flex-direction: row;
    gap: 8px;
  `}
`;

const InputsWrapper = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  flex: 1;

  ${MEDIA_QUERIES.tabletAndDesktop`
    flex-direction: row;
  `}
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 56px;
  font-size: 16px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 140px;
  `}
`;

const StyledInput = styled(InputText)`
  flex: 1;
  input {
    height: 56px;
    font-size: 16px;
  }
`;

const StyledButton = styled(Button)`
  height: 56px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 148px;
  `}
`;

const ErrorMessage = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  ${font('main', { fontSize: '1.2rem', lineHeight: '147%', fontWeight: 400 })};
  color: ${themedColor('red')};
  margin-top: 8px;

  circle {
    stroke: #f7274a;
    fill: #f7274a;
  }
  path {
    stroke: white;
    fill: white;
  }
`;

const BackgroundImage = styled(Image)`
  position: absolute;
  bottom: calc(env(safe-area-inset-bottom, 0px) - 1px);
  left: 50%;
  transform: translateX(-50%);
  transform-origin: bottom left;
  min-width: 100%;
  height: auto;
  pointer-events: none;

  ${MEDIA_QUERIES.mobile`
    --aspect-ratio: tan(atan2(100dvh, 100vw));
    --t: clamp(0, calc((var(--aspect-ratio) - 1.8) / 0.5), 1);
    --scale: calc(0.5 + 0.3 * var(--t));
    --translate-x: calc(-45% + 8% * var(--t));
    
    transform: scale(var(--scale)) translateX(var(--translate-x));
  `}
`;

export const FSDWaitlist: FC = () => {
  const { region } = useGeoLookupContext();
  const { trackEvent } = useAnalyticsContext();
  const [selectedState, setSelectedState] = useState(() => {
    const code = region?.toLowerCase();
    return code && code in US_STATES ? code : 'ny';
  });
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');

  const isValidEmail = useMemo(() => EMAIL_REGEX.test(email), [email]);
  const isFormValid = useMemo(() => isValidEmail && selectedState, [isValidEmail, selectedState]);
  const isSuccess = status === 'success';
  const isError = status === 'error';
  const isLoading = status === 'loading';

  const handleSubscribe = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (isLoading || !isFormValid) return;

      const state = selectedState.toUpperCase();

      setStatus('loading');
      trackEvent('fsd_page.subscribe_clicked', { state });

      try {
        await Client.post(`${API_PREFIX_LEGACY}/subscribe`, {
          email,
          state,
          source: 'car',
          product: 'car',
          origin: 'fsd-waitlist',
        });
        setEmail('');
        setStatus('success');
      } catch (error) {
        console.error(error);
        setStatus('error');
      }
    },
    [email, selectedState, isFormValid, isLoading, trackEvent],
  );

  return (
    <Container>
      <FormSection>
        <Title>{isSuccess ? "You're in!" : parse('Lemonade Autonomous&nbsp;Car\nfor Tesla FSD')}</Title>
        <Subtitle>
          {parse(
            preventOneWordInLastLine(
              isSuccess
                ? 'We’ll let you know when FSD savings are ready for you'
                : "Driving is changing, insurance should too. Get notified when we're available in your state.",
            ),
          )}
        </Subtitle>
        {!isSuccess && (
          <>
            <StyledForm onSubmit={handleSubscribe}>
              <InputsWrapper>
                <StyledSelect floatingLabel placeholder="State" value={selectedState} onChange={setSelectedState}>
                  <OptionsList>
                    {Object.entries(US_STATES).map(([code, name]) => (
                      <Option key={code} value={code}>
                        {name}
                      </Option>
                    ))}
                  </OptionsList>
                </StyledSelect>
                <StyledInput
                  type="email"
                  placeholder="Email"
                  floatingLabel
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </InputsWrapper>
              <StyledButton
                type="submit"
                flavor="primary"
                glowing
                disabled={isLoading || !isFormValid}
                isLoading={isLoading}
              >
                {isError ? 'Retry' : "Let's ride"}
              </StyledButton>
            </StyledForm>
            {isError && (
              <ErrorMessage>
                <InfoIcon width="1em" height="1em" />
                Something is wrong. Check your connection and try again.
              </ErrorMessage>
            )}
          </>
        )}
      </FormSection>

      <BackgroundImage src={bgImage} alt="Landscape with car" priority />
    </Container>
  );
};
